import {
  Component,
  inject,
  input,
  Input,
  OnInit,
  signal,
  Type,
  viewChildren,
  InputSignalWithTransform,
  Injector,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  WritableSignal,
  model,
  OnChanges,
  SimpleChanges, effect, OnDestroy, AfterContentInit, AfterContentChecked, DoCheck, AfterViewChecked, ElementRef,
} from '@angular/core';
import * as uuid from 'uuid';
import { TranslateService, Translation } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TableSortDirective } from '@shared/directives/table-sort.directive';
import { SortEvent, TableState } from '@core/interfaces';
import { compare } from '@core/helpers';
import { NgbPagination, NgbPaginationPages } from '@ng-bootstrap/ng-bootstrap';
import { Pagination } from '@core/interfaces/pagination';
import { AnyTableClass, SortColumn, SortDirectionType } from '@core/types';
import { FormsModule } from '@angular/forms';
import { FILTER_ONLY_NUMBERS, ITEMS_PER_PAGE } from '@core/constants';
import { NgComponentOutlet } from '@angular/common';
import { DefaultSearchComponent, InputComponent } from '@shared/components';
import { InputTypes } from '@core/enums';
import { InputModes } from '@core/enums/input-modes';
import { APIService } from '@core/services/api.service';
import { Subject, takeUntil } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { SelectComponent, SelectOptions } from '@shared/components/select/select.component';
import { APIOptions } from '@core/classes/api-options';

export class TableOptions<T> {
  id?: string = uuid.v4();
  name?: string;
  tableClass?: string;
  type?: string = 'Development';
  api?: APIOptions<T>;
  data?: T[];
  autoLoadData?: boolean;
  pagination?: Pagination = {
    itemsPerPage: ITEMS_PER_PAGE,
    pageNumber: 1,
    totalItems: 0,
  };
  ignoreColumns?: string[];
  title?: string;
  actionsComponent?: Type<any> | null;
  interface?: Type<T>; //new (...args: any[]) => Tw
  onPageChange?: (page: number) => void;
  onRowClick?: (row: T) => void;
  renderCustomRow?: (td: HTMLTableCellElement, row: {}, item: string) => any;
  searchComponent?: Type<any> | null;
  extraComponent?: any | null;
  extraLeftComponent?: any | null;
  otherData?: any;

  constructor(args?: TableOptions<T>) {
    this.id = args?.id || this.id;
    this.name = args?.name || '';
    this.tableClass = args?.tableClass || '';
    this.type = args?.type || 'Development';
    this.api = args?.api;
    //this.data?.set(args?.data?.() || []);
    this.data = args?.data;
    this.autoLoadData = args?.autoLoadData || false;
    this.pagination = args?.pagination === undefined ? undefined : (args?.pagination || {
      itemsPerPage: this.data?.length || this.pagination?.itemsPerPage || ITEMS_PER_PAGE,
      pageNumber: 1,
      totalItems: this.data?.length || 0,
    });
    this.ignoreColumns = args?.ignoreColumns
    this.title = args?.title || '';
    this.actionsComponent = args?.actionsComponent || null;
    this.interface = args?.interface;
    this.onPageChange = args?.onPageChange;
    this.onRowClick = args?.onRowClick;
    this.renderCustomRow = args?.renderCustomRow;
    this.searchComponent = args?.searchComponent;
    this.extraComponent = args?.extraComponent;
    this.extraLeftComponent = args?.extraLeftComponent;
    this.otherData = args?.otherData;
  }
}

@UntilDestroy()
@Component({
  selector: 'ncp-table',
  imports: [
    TableSortDirective,
    NgbPagination,
    FormsModule,
    NgbPaginationPages,
    NgComponentOutlet,
    InputComponent,
    SelectComponent,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent<T extends AnyTableClass> implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('extraLeft') public extraLeft!: NgComponentOutlet;
  @ViewChild('extra') public extra!: NgComponentOutlet;

  protected headers = viewChildren(TableSortDirective<T>);

  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  protected translations!: Translation;
  private apiService = inject(APIService);
  private subscription: any;
  private destroy$ = new Subject<void>();
  protected defaultTranslations: Translation;
  protected data: WritableSignal<T[]> = model([]);
  protected pagination: WritableSignal<Pagination> = signal<Pagination>({
    itemsPerPage: ITEMS_PER_PAGE,
    pageNumber: 1,
    totalItems: 0,
  });
  private _state: TableState<T> = {
    pageNumber: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    searchTerm: '',
    searchParams: {},
    sortColumn: '',
    sortDirection: '',
  };
  protected ippSelOptions = signal<SelectOptions>({});
  protected tblInstance = this;

  options = model<TableOptions<T>>(new TableOptions());

  // options = input(new TableOptions<T>(), {
  //   transform: (value: TableOptions<T>) => {
  //     if (!value) {
  //       return new TableOptions<T>({
  //         actionsComponent: UserTableActionsComponent,
  //         searchComponent: DefaultSearchComponent,
  //       });
  //     }
  //
  //     if (value.searchComponent === undefined) {
  //       value.searchComponent = DefaultSearchComponent;
  //     }
  //
  //     this.data.set(value.data || []);
  //     this.pagination.set(value.pagination || this.pagination());
  //     //To avoid initial apiCall request on initialization
  //     //instead of
  //     //this.itemsPerPage = this.pagination().itemsPerPage;
  //     Object.assign(this._state, { itemsPerPage: this.pagination().itemsPerPage });
  //
  //     return value;
  //   },
  // });

  tableEffect = effect(() => {
    this.reload();
  });

  reload = () => {
    if (this.options().searchComponent === undefined) {
      this.options().searchComponent = DefaultSearchComponent;
    }

    this.pagination.set(this.options().pagination || this.pagination());

    if (this.options().data && this.options().pagination) {
      this.data.set(this.options().data!.slice(0, this.pagination().itemsPerPage));
    } else if (this.options().data && !this.options().pagination) {
      this.data.set(this.options().data || []);
    }

    //To avoid initial apiCall request on initialization
    //instead of
    //this.itemsPerPage = this.pagination().itemsPerPage;
    Object.assign(this._state, { itemsPerPage: this.pagination().itemsPerPage });

    if (this.options().autoLoadData) {
      this.requestForData();
    }

    this.ippSelOptions.set(
      new SelectOptions({
        values: [
          { id: 10, value: '10itemsPerPage' },
          { id: 25, value: '25itemsPerPage' },
          { id: 50, value: '50itemsPerPage' },
          { id: 100, value: '100itemsPerPage' },
        ],
        model: this.itemsPerPage,
        onModelChange: this.onItemsPerPageChange,
      })
    );
    //this.changeDetectorRef.detectChanges();
    return this;
  }

  get tableData(): T[] {
    return this.options().data!
  }

  set tableData(value: T[]) {
    let oldOptions = {};
    Object.assign(oldOptions, this.options());
    (oldOptions as TableOptions<T>).data = value;
    (oldOptions as TableOptions<T>)!.pagination!.totalItems = value.length;
    let pagination = this.pagination();
    pagination.totalItems = value.length;
    pagination.pageNumber = this.pagination().pageNumber;
    this.pagination.set(pagination);
    this.options.set(oldOptions);
    this.pageNumber = this.pagination().pageNumber;
    this.detectChanges();
  }

  get state() {
    return this._state;
  }

  get pageNumber() {
    return this.state.pageNumber;
  }

  set pageNumber(pageNumber: number) {
    this.options().pagination!.pageNumber = pageNumber;
    this.pagination().pageNumber = pageNumber;
    this._set({ pageNumber });
  }

  get itemsPerPage() {
    return this.state.itemsPerPage;
  }

  get searchParams() {
    return this.state.searchParams;
  }

  set itemsPerPage(itemsPerPage: number) {
    this._set({ itemsPerPage });
  }

  set searchParams(searchParams: {}) {
    this._set({ searchParams });
  }

  get searchTerm() {
    return this.state.searchTerm;
  }

  set searchTerm(searchTerm: string) {
    this._set({ searchTerm });
  }

  detectChanges = () => {
    this.changeDetectorRef.detectChanges();
  }

  resetPageNumber = () => {
    this.translations = this.translate.instant('tables.' + this.options().type);
    this.pageNumber = 1;
  };

  setPaginationState = (params: { searchTerm: string; pageNumber: number }) => {
    this._set({ searchTerm: params.searchTerm, pageNumber: params.pageNumber });
  };

  setStateParams = (params: {}) => {
    this._set({ searchParams: params, pageNumber: 1 });
  };

  set sortColumn(sortColumn: SortColumn<T>) {
    this._set({ sortColumn });
  }

  set sortDirection(sortDirection: SortDirectionType) {
    this._set({ sortDirection });
  }

  private _set(patch: Partial<TableState<T>>) {
    Object.assign(this._state, { ...this._state, ...patch });

    if (this.options().api) {
      this.requestForData();
    } else if (this.options().data && this.options().pagination) {
      this.data.set(
        this.options().data!.slice(
          (this._state.pageNumber - 1) * this._state.itemsPerPage,
          (this._state.pageNumber - 1) * this._state.itemsPerPage + this._state.itemsPerPage
        )
      );
    }
  }

  protected requestForData = () => {
    if (!this.options().api) {
      return;
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    let argParams: { [key: string]: any } = {};

    this.options()
      .api?.params?.keys()
      .forEach((key) => {
        argParams[key] = this.options().api?.params?.get(key);
      });

    console.log('requestForData', this.state.searchParams, {
      ...{
        items_per_page: this.state.itemsPerPage,
        page_number: this.state.pageNumber,
      },
      ...argParams,
      ...(Object.keys(this.state.searchParams).length > 0
        ? this.state.searchParams
        : this.state.searchTerm
          ? {
              search: this.state.searchTerm,
            }
          : {}),
    });

    this.subscription = this.apiService
      .call({
        path: this.options().api!.path,
        params: new HttpParams({
          fromObject: {
            ...{
              items_per_page: this.state.itemsPerPage,
              page_number: this.state.pageNumber,
            },
            ...argParams,
            ...(Object.keys(this.state.searchParams).length > 0
              ? this.state.searchParams
              : this.state.searchTerm
                ? {
                    search: this.state.searchTerm,
                  }
                : {}),
          },
        }),
      })
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.options().data = res.data;
          this.data.set(res.pagination ? res.data : res);
          if (res.pagination) {
            this.pagination.set(res.pagination);
          }
        }
      });
  };

  ngOnInit(): void {
    this.translations = this.translate.instant('tables.' + this.options().type);
    this.defaultTranslations = this.translate.instant('table');
    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('tables.' + this.options().type);
      this.defaultTranslations = this.translate.instant('table');
    });

    // if (this.options().searchComponent === undefined) {
    //   this.options().searchComponent = DefaultSearchComponent;
    // }
    //
    // this.pagination.set(this.options().pagination || this.pagination());
    //
    // if (this.options().data && this.options().pagination) {
    //   this.data.set(this.options().data!.slice(0, this.pagination().itemsPerPage));
    // } else if (this.options().data && !this.options().pagination) {
    //   this.data.set(this.options().data || []);
    // }
    //
    // //To avoid initial apiCall request on initialization
    // //instead of
    // //this.itemsPerPage = this.pagination().itemsPerPage;
    // Object.assign(this._state, { itemsPerPage: this.pagination().itemsPerPage });
    //
    // if (this.options().autoLoadData) {
    //   this.requestForData();
    // }
    //
    // this.ippSelOptions.set(
    //   new SelectOptions({
    //     values: [
    //       { id: 10, value: '10itemsPerPage' },
    //       { id: 25, value: '25itemsPerPage' },
    //       { id: 50, value: '50itemsPerPage' },
    //       { id: 100, value: '100itemsPerPage' },
    //     ],
    //     model: this.itemsPerPage,
    //     onModelChange: this.onItemsPerPageChange,
    //   })
    // );
  }

  ngAfterViewInit() {
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.tableEffect.destroy();
  }

  protected onSort({ column, direction }: SortEvent<T>) {
    if (this.options().pagination) {
      return;
    }
    // resetting other headers
    this.headers().map((header) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    if (this.data()) {
      // sorting countries
      if (direction === '' || column === '') {
        this.data.set(this.options().data || []);
      } else {
        this.data.set(
          [...this.data()].sort((a, b) => {
            const res = compare(a[column], b[column]);
            return direction === 'asc' ? res : -res;
          })
        );
      }
    }
  }

  protected onPaginationInputKeyUp = (event: KeyboardEvent, value: string): void => {
    if (event.key === 'Enter') {
      if (!isNaN(Number(value))) {
        this.pageNumber = Number(value);
      }
    }
  };

  protected onPaginationInputBlur = (value: string): void => {
    if (!isNaN(Number(value))) {
      this.pageNumber = Number(value);
    }
  };

  protected onSearchInputKeyUp = (event: KeyboardEvent, value: string): void => {
    if (event.key === 'Enter') {
      this.searchTerm = value;
    }
  };

  protected onSearchInputBlur = (value: string): void => {
    if (!isNaN(Number(value))) {
      this.searchTerm = value;
    }
  };

  protected onPaginationInput = (input: HTMLInputElement): any => {
    return input.value.replace(FILTER_ONLY_NUMBERS, '');
  };

  protected onItemsPerPageChange = (value?: number | string) => {
    //this.pageNumber = 1;
    this._set({ itemsPerPage: <number>value, pageNumber: 1 });
  };

  protected formatInput(input: HTMLInputElement) {
    input.value = input.value.replace(FILTER_ONLY_NUMBERS, '');
  }

  protected readonly Object = Object;
  protected readonly InputTypes = InputTypes;
  protected readonly InputModes = InputModes;
  protected readonly FILTER_ONLY_NUMBERS = FILTER_ONLY_NUMBERS;
  protected readonly Type = Type;
  protected readonly DefaultSearchComponent = DefaultSearchComponent;
}
